import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '@/lib/auth/authStore';
import { queryClient } from '@/lib/api/queryClient';
import { queryKeys } from '@/lib/api/queryKeys';
import { registerFcm } from '@/lib/notifications/registerFcm';

/**
 * Mounted once inside AdminShell. While the user is authenticated it:
 *   1. Registers an FCM token (idempotent, silent if config missing).
 *   2. Opens a Socket.IO connection to /notifications and invalidates the
 *      unread + inbox queries whenever the server emits notification:new or
 *      notification:read.
 *
 * Disconnects + tears down on logout.
 */
export function NotificationsInitializer() {
  const token = useAuthStore((s) => s.token);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    // FCM registration runs once per session — idempotent across mounts.
    void registerFcm();

    const apiBase = import.meta.env.VITE_API_URL ?? '';
    // VITE_API_URL is `http://host:port/api/v1` — Socket.IO wants the host root.
    const host = apiBase.replace(/\/api\/v1\/?$/, '');
    const namespace = `${host}/notifications`;

    const socket = io(namespace, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.inbox });
    };
    socket.on('notification:new', refresh);
    socket.on('notification:read', refresh);

    return () => {
      socket.off('notification:new', refresh);
      socket.off('notification:read', refresh);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  return null;
}
